/**
 * AttackResolver — orchestrates a full attack sequence:
 * extracts tiles, delegates to AttackType, applies damage per-hit,
 * triggers visual effects, and reports completion.
 *
 * Targets a specific enemy by index (slot).
 */

import { AttackType, AttackContext, AttackTarget } from "./AttackType";
import { BattleHUD } from "../BattleHUD";
import { CombatManager } from "../CombatManager";
import { floatingText, flash, shake } from "../../../../framework/effects";

export interface AttackCallbacks {
  onAttackComplete: (targetDead: boolean) => void;
}

export class AttackResolver {
  constructor(
    private scene: any,
    private hud: BattleHUD,
    private combat: CombatManager,
  ) {}

  resolve(
    word: string,
    damageResult: { damage: number; wordScore: number; attackBonus: number },
    attackType: AttackType,
    targetIndex: number,
    callbacks: AttackCallbacks,
  ): void {
    const tiles = this.hud.extractTiles(word);
    const enemy = this.combat.enemies[targetIndex];
    const slot = this.hud.enemySlots[targetIndex];

    const target: AttackTarget = {
      sprite: slot.sprite,
      x: slot.x,
      y: slot.y,
    };

    const ctx: AttackContext = {
      scene: this.scene,
      tiles,
      target,
      wordScore: damageResult.wordScore,
      attackBonus: damageResult.attackBonus,
      totalDamage: damageResult.damage,
    };

    let targetDead = false;

    attackType.execute(
      ctx,
      // onHit — called per impact
      (damage: number, x: number, y: number) => {
        if (targetDead) return;

        const hit = this.combat.dealDamage(enemy, damage);
        this.hud.updateEnemyHp(targetIndex, enemy.hp, enemy.maxHp);
        this.hud.updateEnemyArmor(targetIndex, enemy.armor);

        // Show HP damage
        if (hit.hpDamage > 0) {
          floatingText(this.scene, {
            text: `-${hit.hpDamage}`,
            x,
            y,
            color: "#fbbf24",
            fontSize: hit.hpDamage >= 5 ? 22 : 16,
          });
        }
        // Show armor absorbed
        if (hit.absorbed > 0) {
          floatingText(this.scene, {
            text: `${hit.absorbed} blocked`,
            x,
            y: y - 16,
            color: "#94a3b8",
            fontSize: 12,
          });
        }

        flash(this.scene, slot.sprite);
        shake(this.scene, slot.sprite, {
          intensity: Math.min(2 + hit.hpDamage, 6),
        });

        if (hit.dead) {
          targetDead = true;
        }
      },
      // onComplete — called once when all hits are done
      () => {
        callbacks.onAttackComplete(targetDead);
      },
    );
  }
}
